import React, { useEffect, useRef, useContext } from 'react';
import { useHistory, useLocation } from 'react-router';
import { AppStateContext, useSetState } from '../../AppState';
import { CustomViewContext } from '../toolbar/CustomViewContextProvider';
import { getCustomViewDropdownFilters, getSortOrderFromStorage, setSelectedCustomView } from '../toolbar/CustomViewPopup/heplers/customPopupHelper';
import { ListContext } from './ListContextProvider';
import ListFunctions from './ListFunctions';
import { FILTERS } from '../../shared/utils/constants';
import { getCachedUsers } from '../../shared/cache/PlatformUserCache';

interface Props {
  defaultFilters: FilterPropsItems[];
  isBoardView?: boolean;
  setParentFilteredTickets?: React.Dispatch<React.SetStateAction<any>>;
  allTickets?: {
    active: any[];
    closed: any[];
    all: any[];
  };
}

const useCustomViewsTickets = ({ defaultFilters, isBoardView, setParentFilteredTickets, allTickets }: Props) => {
  const state = useContext(AppStateContext);
  const setState = useSetState();
  const { setListStates } = useContext(ListContext);
  const { getpaginatedTickets, customViewId, setTicketInterval } = ListFunctions();
  const { customViewStates, setCustomViewStates } = useContext(CustomViewContext);

  const history = useHistory();
  const location = useLocation();

  const firstCall = useRef(false);
  const isMounted = useRef(false);
  // Set by the initial-load effect below when it actually fires the first paginated-tickets
  // call for this mount, and consumed (reset) by the very next run of the update effect so it
  // doesn't also fire a redundant/incomplete call in the same commit. Deliberately a local ref
  // rather than app state: state.listFilter/state.filtersApplyCount live in a never-unmounted
  // provider and can carry stale values in from an earlier page, which used to make both effects
  // think it was safe (or unsafe) to fetch based on the wrong signal.
  const initialLoadHandled = useRef(false);

  const setIdOfRequestersFromPlatformUsers = async (FiltersDropdownValues: FilterPropsItems[] | undefined) => {
    if (!FiltersDropdownValues) return;
    let requesterFilterValues = FiltersDropdownValues.find(dropdownValue => dropdownValue.title === FILTERS.REQUESTERS.title);
    if (!requesterFilterValues) return;

    const users = await getCachedUsers(requesterFilterValues.values?.map(v => v.key) ?? []);
    requesterFilterValues.values?.forEach(value => {
      const platformuser = users.find(user => user.Id === value.key)
      value.aadObjectId = platformuser?.AadObjectId;
      value.title = platformuser?.UserName;
    });
  };

  useEffect(() => {
    // Reset every time this effect actually re-evaluates, so a flag left set by an earlier,
    // unrelated firing of this effect can never be misread by the update effect below as
    // "just handled in this commit".
    initialLoadHandled.current = false;

    if (isBoardView && (!allTickets || allTickets.all.length <= 0)) return;

    const currentPage = 1;

    if (firstCall.current) return;

    if (!customViewId || customViewStates.customViews.length <= 0) return;
    const customView = customViewStates.customViews.find((customView) => customView.Id === Number(customViewId));

    let FiltersDropdownValues: FilterPropsItems[] | undefined;

    const sortOrder: ISortOrder = customView?.OrderBy
      ? { orderBy: customView.OrderBy, orderType: customView.OrderType }
      : null;
    const updatedSortOrder = getSortOrderFromStorage() || sortOrder;

    if (customView) {
      FiltersDropdownValues = getCustomViewDropdownFilters(JSON.parse(JSON.stringify(defaultFilters)), customView.ViewFilters);
      setSelectedCustomView({
        customView,
        defaultFilters,
        setCustomViewStates,
        history,
        location,
        sortOrderUponRefresh: updatedSortOrder
      });
    } else {
      setCustomViewStates((prev): CustomViewStatesType => ({
        ...prev,
        selectedCustomView: undefined,
        configureColumnsApplied: {
          prevSortOrder: updatedSortOrder,
          sortOrder: updatedSortOrder
        }
      }));
    }

    if (isBoardView) {
      (async () => {
        await setIdOfRequestersFromPlatformUsers(FiltersDropdownValues);
        // The board's per-lane fetch (Board.getTicketsWithDetails) already applied the view /
        // default-view / child predicate server-side, so allTickets is the matching set.
        setParentFilteredTickets(allTickets.all);
      })();
    } else {
      getpaginatedTickets({ page: currentPage, FiltersDropdownValues, sortOrder: updatedSortOrder });
    }
    firstCall.current = true;
    isMounted.current = true;
    initialLoadHandled.current = true;

  }, [customViewStates?.customViews, allTickets?.all]);

  useEffect(() => {
    // This useEffect will run on click refresh button also
    const currentPage = 1;
    let timer = null;

    if (!isBoardView) {
      setListStates((prev) => ({
        ...prev,
        isTicketsFetching: true,
        errorDuringFetch: false,
        currentPage,
      }));
    }

    // on page loading, if search_params or localstorage contain customview id then some data needs to be fetched from backend before calling getpaginatedTickets. this functionality is handled in above useEffect.
    // Defer to it: either it already ran in this exact commit (initialLoadHandled), or it's still
    // waiting on customViewStates.customViews to load (in which case it hasn't set firstCall yet).
    if (initialLoadHandled.current) {
      initialLoadHandled.current = false;
      return;
    }
    if (customViewId && !firstCall.current && customViewStates.customViews.length <= 0) return;

    if (isBoardView && (allTickets?.all?.length ?? -1) >= 0) {
      (async () => {
        await setIdOfRequestersFromPlatformUsers(customViewStates.selectedCustomView?.FiltersDropdownValues);
        // Server-filtered already — see the note in the effect above.
        setParentFilteredTickets(allTickets.all);
      })();
    }
    if (!isBoardView) {
      if (!isMounted.current) {
        setCustomViewStates(prev => ({
          ...prev,
          configureColumnsApplied: {
            prevSortOrder: getSortOrderFromStorage(),
            sortOrder: getSortOrderFromStorage(),
          }
        }));
      }

      getpaginatedTickets({
        page: currentPage,
        ...(!isMounted.current && {
          defaultFilters,
        }),
      });
      isMounted.current = true
      if (timer) clearInterval(timer);
      timer = setTicketInterval();
    }

    return () => { if (timer) clearInterval(timer); }
  }, [
    state.listFilter, // on refresh this state gets new address in memory
    state.filtersApplyCount,
    allTickets?.all
  ]);

  const isWholeNumber = (num: number | undefined) => {
    if (!num) return false;
    return num % 1 === 0;
  }

  useEffect(() => {
    return () => {
      setState(prev => ({
        ...prev,
        listFilter: isWholeNumber(prev.listFilter) ? prev.listFilter : undefined,
      }));
    };
  }, []);

  return <></>;
};

export default useCustomViewsTickets;